import React, { useState, useEffect } from 'react';
import { useGetAiWorkerUsageByIdMutation, useGetAiWorkerByIdQuery, useTypedSelector } from '@circuitry-ai/doc-data';
import { Icon } from '@iconify/react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { UXGenieState } from '../../modules/shared/types/src/lib/template-types';
import { useTemplate } from '../contexts/TemplateContext';
import { cleanGeneratedCode, isHtmlContent } from './uxgenie/utils';
import { UXGenieHeader } from './uxgenie/components/UXGenieHeader';
import { UXGenieSplitPanel } from './uxgenie/components/UXGenieSplitPanel';
import { useUxgenieTemplateHtmlResolver } from '@circuitry-ai/doc-ui';


const UXGenie: React.FC<{ aiworkerId?: string; basePath?: string }> = ({ aiworkerId: propAiworkerId, basePath: propBasePath }) => {
  const basePath = propBasePath ?? '/uxgenie';
  const location = useLocation();
  const navigate = useNavigate();
  const templateContext = useTemplate();
  const { aiworker_id: routeAiworkerId, usage_id: routeUsageId } = useParams<{ aiworker_id?: string; usage_id?: string }>();

  const effectiveAiworkerId = propAiworkerId || routeAiworkerId;
  const navState = location.state as (UXGenieState & { action?: any; uxgenieWorker?: any; template?: any }) | undefined;

  const token = useTypedSelector((state: any) => state.auth.token) as string | null;

  // Orchestrator state
  const [uxgenieWorker, setUxgenieWorker] = useState<any>(null);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);

  // Initial data passed down to UXGenieSplitPanel
  const [initialCode, setInitialCode] = useState<string | undefined>();
  const [initialData, setInitialData] = useState<string | undefined>();
  const [initialActionName, setInitialActionName] = useState<string | null>(null);
  const [initialActionId, setInitialActionId] = useState<string | null>(null);
  const [initialActionData, setInitialActionData] = useState<any>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isReadOnlyMode, setIsReadOnlyMode] = useState(false);
  const [isViewingTemplate, setIsViewingTemplate] = useState(false);

  const [getTemplateById] = useGetAiWorkerUsageByIdMutation();
  const { resolveHtml } = useUxgenieTemplateHtmlResolver();

  // Fetch worker by ID when not available from nav state
  const { data: fetchedWorkerData } = useGetAiWorkerByIdQuery(effectiveAiworkerId || '', {
    skip: !effectiveAiworkerId || !token,
  });

  useEffect(() => {
    if (fetchedWorkerData && !uxgenieWorker) {
      const workerData = (fetchedWorkerData as any)?.data ?? fetchedWorkerData;
      setUxgenieWorker(workerData);
    }
  }, [fetchedWorkerData, uxgenieWorker]);

  // --- Process navigation state ---
  useEffect(() => {
    if (navState?.uxgenieWorker) {
      setUxgenieWorker(navState.uxgenieWorker);
    }
    if (navState?.action) {
      setInitialActionData(navState.action);
      setInitialActionName(navState.action.name);
    }
    if ((navState as any)?.readOnly) {
      setIsReadOnlyMode(true);
    }

    const isEdit = location.pathname.includes('/edit/') || (navState as any)?.editMode === true;
    if (isEdit) setIsEditMode(true);

    // If edit mode with routeUsageId, let the fetch effect handle it
    if (isEdit && routeUsageId) return;

    if (navState?.template) {
      void extractAndSetTemplate(navState.template, isEdit);
    }
  }, [navState, location.pathname, routeUsageId]);

  // --- Fetch template by routeUsageId (edit mode via URL) ---
  useEffect(() => {
    const isEditModeFromUrl = location.pathname.includes('/edit/');
    if (!isEditModeFromUrl || !routeUsageId) return;

    setIsEditMode(true);
    setIsLoadingTemplate(true);

    // Track staleness separately from `.abort()` — abort rejects asynchronously, so
    // without this flag a superseded request's `.finally()` can still fire (and flip
    // isLoadingTemplate back to false) after the next effect run already started
    // loading the new one. Without either guard, an effect re-run (React StrictMode's
    // dev double-invoke, or a remount) also lets the stale request run to completion,
    // producing duplicate network calls for the same usage id.
    let isStale = false;
    const request = getTemplateById(routeUsageId);

    request
      .unwrap()
      .then((response) => {
        let templateData = response;
        if (Array.isArray(response) && response.length > 0) {
          templateData = response[0];
        } else if (response?.data && Array.isArray(response.data) && response.data.length > 0) {
          templateData = response.data[0];
        } else if (response?.data && !Array.isArray(response.data)) {
          templateData = response.data;
        }
        return extractAndSetTemplate(templateData, true);
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return;
        console.error('❌ Failed to fetch template:', error);
      })
      .finally(() => {
        if (!isStale) setIsLoadingTemplate(false);
      });

    return () => {
      isStale = true;
      request.abort();
    };
  }, [routeUsageId, location.pathname]);

  // --- Initialize from location data (data passed via navState.data) ---
  useEffect(() => {
    if (navState?.data) {
      const newData = typeof navState.data === 'string' ? navState.data : JSON.stringify(navState.data, null, 2);
      setInitialData(newData);
    }
  }, [navState]);

  const extractAndSetTemplate = async (template: any, isEdit: boolean) => {
    const outputData = template.output_data || template.action_output;
    let generatedCode = outputData?.generated_code;

    if (!generatedCode && outputData?.output_type === 'html' && outputData?.storage === 's3') {
      const aiworker_id = template.aiworker_id || effectiveAiworkerId;
      const aiworker_usage_id = template.aiworker_usage_id || routeUsageId;
      if (!aiworker_id || !aiworker_usage_id) return;
      try {
        generatedCode = await resolveHtml({ aiworker_id, aiworker_usage_id, fileName: outputData.html_file_name });
      } catch (error) {
        console.error('❌ Failed to resolve saved HTML template from storage:', error);
        return;
      }
    }

    if (!generatedCode) return;

    const savedContent = outputData.output_type === 'html' || isHtmlContent(generatedCode)
      ? generatedCode
      : cleanGeneratedCode(generatedCode);
    const originalData = template.input_data?.original_data || outputData.generated_schema || '';

    setInitialCode(savedContent);
    if (originalData) setInitialData(originalData);
    setInitialActionName(outputData.action_name || template.agent_name || null);
    if (template.input_data?.user_input) setInitialActionId(template.input_data.user_input);
    setIsViewingTemplate(true);
  };

  const handleBack = () => {
    const workerId = routeAiworkerId || uxgenieWorker?.aiworker_id;
    if (workerId) navigate(`${basePath}/${workerId}?tab=templates`);
  };

  if (!token) {
    return (
      <div className="flex items-center justify-center h-full text-default-500">
        <p>Please log in to use UXGenie.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-content1 relative overflow-hidden">
      <UXGenieHeader
        uxgenieWorker={uxgenieWorker}
        routeAiworkerId={routeAiworkerId}
        isViewingTemplate={isViewingTemplate}
        hasGeneratedTemplate={isEditMode}
        currentActionName={initialActionName}
        onBack={handleBack}
      />

      {isLoadingTemplate ? (
        <div className="flex items-center justify-center flex-1 text-blue-600">
          <Icon icon="eos-icons:loading" width={24} />
          <span className="ml-2">Loading template...</span>
        </div>
      ) : (
        <UXGenieSplitPanel
          isEdit={isEditMode}
          isReadOnly={isReadOnlyMode}
          initialCode={initialCode}
          initialData={initialData}
          initialActionName={initialActionName}
          initialActionId={initialActionId}
          initialUsageId={routeUsageId}
          initialActionData={initialActionData}
          token={token}
          worker={uxgenieWorker}
          onSaveComplete={(workerId) => navigate(`${basePath}/${workerId}?tab=templates`)}
        />
      )}
    </div>
  );
};

export default UXGenie;
